import DOMPurify from "dompurify";
import { DeletedContainerChange } from "@/models/consolidation";

/**
 * Marks a re-inserted deleted container. The element is a normal changed
 * container (`[data-container-type][id]`) so it flows through the existing
 * highlight / grouping / navigation / click machinery, plus this class so it can
 * be styled (red strikethrough) and gated by the highlight toggle.
 */
export const CONSOLIDATION_DELETED_GHOST_CLASS = "consolidation-deleted-ghost";

const escapeId = (id: string): string =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(id)
    : id.replace(/["\\]/g, "\\$&");

/**
 * Re-inserts deleted containers into the consolidated HTML as "ghost" elements
 * at their original blueprint position, so the viewer can show what an amendment
 * removed. Each ghost mirrors the live container contract (`id` +
 * `data-container-type`) and carries the last non-empty HTML it held.
 *
 * Ghosts are anchored after the nearest preceding surviving container
 * (`afterContainerId`), falling back to before the nearest following survivor
 * (`beforeContainerId`). Multiple ghosts sharing an anchor keep their blueprint
 * order. The transform is applied before the HTML is parsed into React, so React
 * owns the ghost nodes.
 */
export const injectDeletedContainerGhostsHtml = (
  html: string,
  deletedChanges: readonly DeletedContainerChange[] | undefined
): string => {
  if (
    !html ||
    !deletedChanges ||
    deletedChanges.length === 0 ||
    typeof DOMParser === "undefined"
  ) {
    return html;
  }

  const doc = new DOMParser().parseFromString(
    `<div id="__cc_ghost_root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("__cc_ghost_root");
  if (!root) return html;

  const ordered = [...deletedChanges].sort(
    (a, b) => a.blueprintIndex - b.blueprintIndex
  );

  // Preserve order when several ghosts share the same preceding anchor: each new
  // ghost is inserted after the previously inserted one for that anchor.
  const lastInsertedByAnchorId = new Map<string, HTMLElement>();

  for (const change of ordered) {
    if (!change.containerId || !change.priorHtml) continue;

    const ghost = doc.createElement("div");
    ghost.setAttribute("id", change.containerId);
    ghost.setAttribute("data-container-type", change.type ?? "");
    ghost.setAttribute("data-consolidation-deleted", "true");
    ghost.setAttribute("class", CONSOLIDATION_DELETED_GHOST_CLASS);
    ghost.innerHTML = DOMPurify.sanitize(change.priorHtml);

    const after = change.afterContainerId
      ? root.querySelector<HTMLElement>(`#${escapeId(change.afterContainerId)}`)
      : null;
    if (after?.parentNode) {
      const refNode =
        lastInsertedByAnchorId.get(change.afterContainerId ?? "") ?? after;
      refNode.parentNode?.insertBefore(ghost, refNode.nextSibling);
      lastInsertedByAnchorId.set(change.afterContainerId ?? "", ghost);
      continue;
    }

    const before = change.beforeContainerId
      ? root.querySelector<HTMLElement>(`#${escapeId(change.beforeContainerId)}`)
      : null;
    if (before?.parentNode) {
      before.parentNode.insertBefore(ghost, before);
      continue;
    }

    root.appendChild(ghost);
  }

  return root.innerHTML;
};
